import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { RazorpayProvider } from './contexts/RazorpayContext';
import { ScrollProvider, ScrollToTop } from './contexts/ScrollContext';
import { useAuth } from './contexts/AuthContext';
import { Helmet } from 'react-helmet';

// Layout components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Critical pages (immediate load)
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import RefundPolicy from './pages/RefundPolicy';

// Lazy loaded pages
const Branches = lazy(() => import('./pages/Branches'));
const BranchDetail = lazy(() => import('./pages/BranchDetail'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const BookingConfirmation = lazy(() => import('./pages/BookingConfirmation'));
const Menu = lazy(() => import('./pages/Menu'));
const Events = lazy(() => import('./pages/Events'));
const Gallery = lazy(() => import('./pages/Gallery'));
const AddOnSelectionPage = lazy(() => import('./pages/AddOnSelectionPage'));
const BookingDetailsPage = lazy(() => import('./pages/BookingDetailsPage'));
const Contact = lazy(() => import('./pages/Contact'));

// Protected route component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { currentUser, isAdmin } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  if (isAdmin && !requireAdmin && window.location.pathname === '/dashboard') {
    return <Navigate to="/admin" />;
  }

  return children;
};

// Page layout wrapper with loading fallback
const PageWrapper = ({ children, setContentLoaded }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // No need to scroll to top here since ScrollToTop component handles it

    setIsLoading(true);
    setContentLoaded(false);

    // Use a small delay to prevent flash of loading state for fast loads
    const timer = setTimeout(() => {
      setIsLoading(false);
      setContentLoaded(true);
    }, 300);

    return () => {
      clearTimeout(timer);
      setContentLoaded(false);
      setIsLoading(true);
    };
  }, [setContentLoaded]);

  if (isLoading) {
    return (
      <div className="flex-grow flex items-center justify-center min-h-[calc(100vh-5rem)] fixed inset-0 bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: 'var(--primary)' }}></div>
      </div>
    );
  }

  return (
    <div className="flex-grow pt-24">
      <Suspense fallback={
        <div className="flex-grow flex items-center justify-center min-h-[calc(100vh-5rem)] fixed inset-0 bg-white">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: 'var(--primary)' }}></div>
        </div>
      }>
        {children}
      </Suspense>
    </div>
  );
};

function App() {
  const [contentLoaded, setContentLoaded] = useState(false);

  return (
    <Router>
      <ScrollProvider>
        <ScrollToTop />
        <AuthProvider>
          <RazorpayProvider>
            <div className="flex flex-col min-h-screen">
              <Helmet>
                <title>Happy Bubble - Event Booking in Bangalore</title>
                <meta name="description" content="Book party halls, event spaces, and celebration venues in Bangalore. Happy Bubble offers premium venues with modern amenities for all your special occasions." />
                <meta name="keywords" content="party hall booking, event space bangalore, celebration venue, party celebration, Happy Bubble, event booking" />
                <meta property="og:title" content="Happy Bubble - Event Booking in Bangalore" />
                <meta property="og:description" content="Premium party halls and event spaces in Bangalore. Book your perfect celebration venue with Happy Bubble." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://happybubble.co.in" />
                <meta property="og:image" content="/icon.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Happy Bubble - Event Booking in Bangalore" />
                <meta name="twitter:description" content="Premium party halls and event spaces in Bangalore. Book your perfect celebration venue with Happy Bubble." />
                <link rel="canonical" href="https://happybubble.co.in" />
                <meta name="robots" content="index, follow" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="theme-color" content="#FF6B6B" />
                <meta name="geo.region" content="IN-KA" />
                <meta name="geo.placename" content="Bangalore" />
                <meta name="geo.position" content="12.9716;77.5946" />
                <meta name="ICBM" content="12.9716, 77.5946" />
              </Helmet>
              <Navbar />
              <main className="flex-grow flex flex-col">
                <Routes>
                  <Route path="/" element={<PageWrapper setContentLoaded={setContentLoaded}><Home /></PageWrapper>} />
                  <Route path="/branches" element={<PageWrapper setContentLoaded={setContentLoaded}><Branches /></PageWrapper>} />
                  <Route path="/branches/:branchId" element={<PageWrapper setContentLoaded={setContentLoaded}><BranchDetail /></PageWrapper>} />
                  <Route path="/menu" element={<PageWrapper setContentLoaded={setContentLoaded}><Menu /></PageWrapper>} />
                  <Route path="/events" element={<PageWrapper setContentLoaded={setContentLoaded}><Events /></PageWrapper>} />
                  <Route path="/gallery" element={<PageWrapper setContentLoaded={setContentLoaded}><Gallery /></PageWrapper>} />
                  <Route path="/about" element={<PageWrapper setContentLoaded={setContentLoaded}><About /></PageWrapper>} />
                  <Route path="/contact" element={<PageWrapper setContentLoaded={setContentLoaded}><Contact /></PageWrapper>} />
                  <Route path="/privacy-policy" element={<PageWrapper setContentLoaded={setContentLoaded}><PrivacyPolicy /></PageWrapper>} />
                  <Route path="/terms-and-conditions" element={<PageWrapper setContentLoaded={setContentLoaded}><TermsAndConditions /></PageWrapper>} />
                  <Route path="/refund-policy" element={<PageWrapper setContentLoaded={setContentLoaded}><RefundPolicy /></PageWrapper>} />
                  <Route path="/add-ons" element={<PageWrapper setContentLoaded={setContentLoaded}><AddOnSelectionPage /></PageWrapper>} />
                  <Route path="/booking-details" element={<PageWrapper setContentLoaded={setContentLoaded}><BookingDetailsPage /></PageWrapper>} />
                  <Route path="/login" element={<PageWrapper setContentLoaded={setContentLoaded}><Login /></PageWrapper>} />
                  <Route path="/signup" element={<PageWrapper setContentLoaded={setContentLoaded}><Signup /></PageWrapper>} />
                  <Route path="/booking-confirmation/:bookingId" element={<PageWrapper setContentLoaded={setContentLoaded}><BookingConfirmation /></PageWrapper>} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <PageWrapper setContentLoaded={setContentLoaded}><UserDashboard /></PageWrapper>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <PageWrapper setContentLoaded={setContentLoaded}><AdminDashboard /></PageWrapper>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </main>
              {contentLoaded && <Footer />}
            </div>
          </RazorpayProvider>
        </AuthProvider>
      </ScrollProvider>
    </Router>
  );
}

export default App;
